/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import EditRowFormFields from "./EditRowFormFields";
import Button, { Icons } from "../buttons/Button";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import { errorAlert, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import InlineLoader from "../loader/InlineLoader";
import {
  EDIT_ROW_TRANSLATIONS,
  LOADING_TEXT_TRANSLATIONS,
  SIDEPANEL_GENERAL_TRANSLATIONS,
} from "../../translations/util";

interface IProps {
  setShowEditPanel: any;
  org: string;
  panelName: string;
  esRef: string;
  initialState: any;
  formFields: any;
  header: string;
  transformResponseRow: any;
  transformUpdateBody?: any;
  onUpdate?: any;
  loadDataByRef: any;
  deleteModalItemName?: any;
  onDelete?: any;
  hasDeletePermission?: boolean;
  hasEditPermission?: boolean;
  refreshTable?: any;
  customValidationFn?: (data: any) => boolean;
  showSmallSidepanel?: boolean;
  ICCheckAPI?: any;
  flagged?: boolean;
  setFlagged?: (val: boolean) => void;
}

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isMappedFieldValueInvalid = (
  data: { [x: string]: string | any[] },
  mappedFieldList: string[]
) => {
  if (!data) return true;
  let isMappedFieldEmpty = true;
  const isDisplayNameEmpty =
    data["DISPLAY-NAME"] === null || String(data["DISPLAY-NAME"]).length === 0;
  const result = mappedFieldList.filter(
    (field: string | number) => data[field]
  );
  if (result.length === 0) {
    return true;
  }
  if (
    data["SELECTED-MAPPED-FIELD"].includes("+") &&
    result.length === 2 &&
    data[result[0]] !== "" &&
    data[result[1]] !== "" &&
    data[result[0]] !== null &&
    data[result[1]] !== null
  ) {
    isMappedFieldEmpty = false;
  } else if (
    data["SELECTED-MAPPED-FIELD"]?.includes("+") &&
    result.length === 1
  ) {
    isMappedFieldEmpty = true;
  } else if (
    !data["SELECTED-MAPPED-FIELD"]?.includes("+") &&
    result.length === 1 &&
    (data["MAPPED-P25-ID"] !== "" ||
      data["MAPPED-DFSI-ID"] !== "" ||
      data["MAPPED-DMR-ID"] !== "" ||
      data["MAPPED-ROIP-ID"] !== "")
  ) {
    isMappedFieldEmpty = false;
  }
  return isDisplayNameEmpty || isMappedFieldEmpty;
};

const isFormInvalid = ({
  data,
  formFields,
  customValidation,
}: {
  data: any;
  formFields: any;
  customValidation?: any;
}) => {
  if (!data) return true;
  const isEditFormValid = formFields
    .map((rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
            return isInValidNumberForField(
              data[field.name],
              field.min,
              field.max
            );
          case "TEXTAREA":
            return field.isInputInvalid?.(data[field.name]);
          case "FILE":
            return (
              data[field.name] === "" ||
              data[field.name] === null ||
              data[field.name] === undefined ||
              (data.TYPE !== undefined &&
                data.TYPE !== "application/x-x509-ca-cert")
            );
          default:
            return false;
        }
      });
      return res.reduce((acc: any, el: any) => acc || el);
    })
    .reduce((acc: any, el: any) => acc || el);

  return isEditFormValid || customValidation;
};

const EditRow = ({
  setShowEditPanel,
  onDelete,
  esRef,
  org,
  panelName,
  initialState,
  formFields,
  header,
  transformResponseRow,
  transformUpdateBody,
  onUpdate,
  loadDataByRef,
  deleteModalItemName,
  hasDeletePermission = false,
  hasEditPermission = false,
  refreshTable,
  customValidationFn = () => false,
  showSmallSidepanel,
  ICCheckAPI,
  flagged,
  setFlagged,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const { token } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [openFileUpload, setOpenFileUpload] = useState(false);
  const [data, setData] = useState(initialState);
  const [newData, setNewData] = useState(
    header === "LMR User"
      ? {
          "LMR-NW-TYPE": "",
          "MAPPED-MCPTT-ID": "",
          "DISPLAY-NAME": "",
          "MUTUAL-AID": false,
        }
      : {}
  );
  const [initialData, setInitialData] = useState(data);
  const [loading, setLoading] = useState(false);

  const getDataByRef = async (ref: string) => {
    if (org && token) {
      setDataLoading(true);
      try {
        const response = await loadDataByRef({
          token,
          org,
          ref,
        });
        setData(transformResponseRow(response));
        setInitialData(transformResponseRow(response));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const updateData = async () => {
    setLoading(true);
    try {
      await onUpdate({
        body: transformUpdateBody(data),
        token,
        ref: esRef,
        org,
      });
      if (refreshTable) {
        refreshTable();
      }
      getDataByRef(esRef);
      successAlert(EDIT_ROW_TRANSLATIONS.EDIT_SUCCESS_MESSAGE);
      setIsEditing(false);
      if (
        header === "MCPTT User" &&
        flagged &&
        !data["MUTUAL-AID"] &&
        setFlagged
      ) {
        setFlagged(false);
      }
      if (
        header === "MCPTT User" &&
        !flagged &&
        data["MUTUAL-AID"] &&
        setFlagged
      ) {
        setFlagged(true);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const updateLMRUserData = async () => {
    setLoading(true);
    try {
      if (initialData["MAPPED-MCPTT-ID"] !== data["MAPPED-MCPTT-ID"]) {
        await ICCheckAPI({
          data: JSON.stringify({ "MAPPED-MCPTT-ID": data["MAPPED-MCPTT-ID"] }),
          token,
          org,
        });
        await onUpdate({
          body: transformUpdateBody(newData),
          token,
          ref: esRef,
          org,
        });
      } else {
        await onUpdate({
          body: JSON.stringify({
            ...(newData &&
              newData["LMR-NW-TYPE"] && {
                "LMR-NW-TYPE": newData["LMR-NW-TYPE"],
              }),
            ...(newData &&
              newData["DISPLAY-NAME"] && {
                "DISPLAY-NAME": newData["DISPLAY-NAME"],
              }),
            ...(newData &&
              newData["MUTUAL-AID"] !== undefined &&
              [true, false].includes(newData["MUTUAL-AID"]) && {
                "MUTUAL-AID": newData["MUTUAL-AID"],
              }),
          }),
          token,
          ref: esRef,
          org,
        });
      }
      if (refreshTable) {
        refreshTable();
      }
      getDataByRef(esRef);
      successAlert(EDIT_ROW_TRANSLATIONS.EDIT_SUCCESS_MESSAGE);
      setIsEditing(false);
      if (flagged && !data["MUTUAL-AID"] && setFlagged) {
        setFlagged(false);
      }
      if (!flagged && data["MUTUAL-AID"] && setFlagged) {
        setFlagged(true);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (header: string) => {
    if (header === "LMR User") {
      return updateLMRUserData;
    }
    return updateData;
  };

  useEffect(() => {
    getDataByRef(esRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esRef]);

  return (
    <>
      <aside
        id="sidepanel"
        data-cy="editRow-sidepanel"
        className={`sidepanel ${
          showSmallSidepanel
            ? "sidepanel-view small-sidepanel"
            : "sidepanel-edit"
        } fixed z-40 h-full transform transition-transform ease-in duration-150 bg-white`}
      >
        <div className="panel-header">
          <div className="panel-left-side">
            <Button
              kind="ghost"
              className="close-btn"
              icon={Icons.Close}
              onClick={() => setShowEditPanel()}
              dataCy="closeEditRow-"
            />
            <h5 data-cy="editRow-panelName"> {panelName} </h5>
          </div>

          {!isEditing ? (
            <div className="panel-right-side tab-header-buttons">
              {onUpdate ? (
                <Button
                  className="w-sm mr-2"
                  kind="tertiary"
                  icon={Icons.Edit}
                  disabled={!hasEditPermission}
                  label={SIDEPANEL_GENERAL_TRANSLATIONS.buttonLabels.edit}
                  onClick={() => {
                    setIsEditing(true);
                  }}
                  dataCy="sidepanelEdit-"
                />
              ) : null}

              {onDelete ? (
                <Button
                  kind="tertiary"
                  className="w-sm"
                  label={SIDEPANEL_GENERAL_TRANSLATIONS.buttonLabels.delete}
                  disabled={!hasDeletePermission}
                  icon={Icons.Delete}
                  onClick={() => {
                    return setOpen(true);
                  }}
                  dataCy="sidepanelDelete-"
                />
              ) : null}
            </div>
          ) : (
            <div className="tab-header-buttons">
              <Button
                className="w-sm mr-2"
                type="button"
                kind="tertiary"
                disabled={false}
                label={SIDEPANEL_GENERAL_TRANSLATIONS.buttonLabels.cancel}
                onClick={() => {
                  setOpenEdit(true);
                }}
                dataCy="sidepanelCancel-"
              />
              <Button
                className="w-sm"
                type="button"
                loading={loading}
                disabled={
                  panelName.trim() === "MCPTT User"
                    ? isMappedFieldValueInvalid(
                        data,
                        Object.keys(data).filter((field) =>
                          field.startsWith("MAPPED")
                        )
                      )
                    : isFormInvalid({
                        data,
                        formFields,
                        customValidation: customValidationFn(data),
                      })
                }
                onClick={handleUpdate(header)}
                label={SIDEPANEL_GENERAL_TRANSLATIONS.buttonLabels.save}
                dataCy="sidepanelSave-"
              />
            </div>
          )}
        </div>
        <div className="panel-content">
          <div className="data-tabs">
            {dataLoading ? (
              <InlineLoader
                description={LOADING_TEXT_TRANSLATIONS.defaultLoadingText}
              />
            ) : (
              <EditRowFormFields
                data={data}
                newData={newData}
                isEditing={isEditing}
                esRef={esRef}
                setData={setData}
                setNewData={setNewData}
                formFields={formFields}
                formModule={header}
                openFileUpload={openFileUpload}
                setOpenFileUpload={setOpenFileUpload}
              />
            )}
          </div>
        </div>
      </aside>
      <DeleteModal
        modalHeaderText={`${EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.header} ${header}`}
        modalDescriptionText={EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.description}
        onDelete={() => {
          setIsEditing(false);
          setData(initialData);
          setOpenEdit(false);
          setOpenFileUpload(false);
        }}
        open={openEdit}
        setOpen={setOpenEdit}
        confirmationButtonLabel={
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.confirmationButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <DeleteModal
        modalHeaderText={`${EDIT_ROW_TRANSLATIONS.DELETE_MODAL.header} ${header}`}
        modalDescriptionText={EDIT_ROW_TRANSLATIONS.DELETE_MODAL.description(
          header,
          data?.[deleteModalItemName]
        )}
        onDelete={() => {
          if (data["MUTUAL-AID"] && setFlagged) {
            setFlagged(false);
          }
          onDelete(esRef);
        }}
        open={open}
        setOpen={setOpen}
      />
    </>
  );
};

export default EditRow;
